import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Sun, BookOpen, MapPin } from "lucide-react";

const SERVICE_IMG = "https://customer-assets.emergentagent.com/job_faith-bubbles-io/artifacts/98s7hwj4_IMG_5902.JPEG";

const services = [
  {
    icon: Sun,
    title: "Sunday Service",
    time: "10:00 AM",
    description: "Join us for a powerful time of worship, prayer, and the Word of God. Experience His presence and leave transformed.",
    accent: "red",
  },
  {
    icon: BookOpen,
    title: "Wednesday Service",
    time: "6:30 PM",
    description: "Midweek service for deeper fellowship and understanding of God's Word. Grow in faith together.",
    accent: "amber",
  },
];

export const ServiceTimes = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="services" data-testid="services-section" className="py-20 md:py-32 bg-gradient-to-br from-stone-50 to-red-50/30 relative overflow-hidden">
      <div className="bubble-decoration bg-red-400 w-64 h-64 -top-32 -left-32 animate-pulse-soft" />
      <div className="bubble-decoration bg-amber-300 w-48 h-48 -bottom-24 right-20 animate-pulse-soft" style={{ animationDelay: "1s" }} />

      <div className="max-w-7xl mx-auto px-6 md:px-12" ref={ref}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="font-accent text-2xl text-amber-600">Join Us</span>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-stone-900 mt-2">
            Service <span className="text-red-600">Times</span>
          </h2>
          <p className="text-base md:text-lg text-stone-600 mt-4 max-w-2xl mx-auto">
            Come and experience the power of God in our services. Everyone is welcome.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8 items-start">
          {/* Service cards */}
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-6">
            {services.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.2 + i * 0.15 }}
                className="bubbly-card bg-white p-8 rounded-[2rem] border border-stone-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] relative overflow-hidden group"
                data-testid={`service-card-${s.accent}`}
              >
                {/* Corner accent */}
                <div className={`absolute top-0 right-0 w-24 h-24 rounded-bl-[3rem] ${
                  s.accent === "red" ? "bg-red-50" : "bg-amber-50"
                } transition-colors duration-300 group-hover:${
                  s.accent === "red" ? "bg-red-100" : "bg-amber-100"
                }`} />

                <div className={`relative z-10 w-14 h-14 rounded-2xl flex items-center justify-center mb-6 ${
                  s.accent === "red" ? "bg-red-600 text-white" : "bg-amber-500 text-white"
                }`}>
                  <s.icon size={26} />
                </div>
                <h3 className="relative z-10 text-xl font-bold text-stone-900 mb-2">{s.title}</h3>
                <div className={`relative z-10 inline-block rounded-full px-4 py-1.5 mb-4 text-sm font-bold ${
                  s.accent === "red" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"
                }`}>
                  {s.time}
                </div>
                <p className="relative z-10 text-stone-600 text-sm leading-relaxed">{s.description}</p>
              </motion.div>
            ))}

            {/* Location card with Google Maps */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="sm:col-span-2 bubbly-card rounded-[2rem] shadow-[0_8px_30px_rgb(220,38,38,0.2)] relative overflow-hidden"
              data-testid="location-card"
            >
              {/* Map */}
              <div className="w-full h-48 sm:h-56">
                <iframe
                  title="Winners Chapel Ipswich Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2446.8!2d1.1784!3d52.0475!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47d9a1c1c1c1c1c1%3A0x0!2sGainsborough+Sports+Centre%2C+5+Braziers+Wood+Rd%2C+Ipswich+IP3+0SP!5e0!3m2!1sen!2suk!4v1709000000000"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="rounded-t-[2rem]"
                />
              </div>
              {/* Info overlay */}
              <div className="bg-red-600 text-white p-6 rounded-b-[2rem] relative">
                <div className="bubble-decoration bg-white w-40 h-40 -top-20 -right-20 opacity-10" />
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center shrink-0">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-1">Our Location</h3>
                    <p className="text-red-100 text-sm leading-relaxed">
                      Gainsborough Sports Centre<br />
                      5 Braziers Road, Ipswich, IP3 0SP
                    </p>
                    <a
                      href="https://maps.google.com/?q=Gainsborough+Sports+Centre+5+Braziers+Road+IP3+0SP"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mt-3 bg-white text-red-600 rounded-full px-6 py-2.5 text-sm font-semibold hover:shadow-lg transition-transform duration-200 hover:-translate-y-0.5"
                      data-testid="get-directions-btn"
                    >
                      Get Directions
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Service image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="hidden lg:block"
          >
            <div className="rounded-[2.5rem] overflow-hidden shadow-[0_20px_50px_rgb(0,0,0,0.1)] sticky top-32">
              <img
                src={SERVICE_IMG}
                alt="Live worship service"
                className="w-full h-[500px] object-cover"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
